"use client";

import { useEffect, useRef, useState } from "react";
import { PATHS, pathValue, bassLevel } from "./spatialPaths";
import type { SpatialScene } from "./spatialScene";

/* The extension's real presets (audio-spat/content.js PRESETS): bass/clarity/
   reverb + a motion pattern, with default speed/intensity. `pattern` maps to a
   3D path; the sliders can override speed/intensity, which flips the dropdown to
   "Custom" exactly like the popup. */
type Preset = { label: string; path: keyof typeof PATHS; bass: number; clarity: number; reverb: number; speed: number; intensity: number };

const PRESETS: Record<string, Preset> = {
  "8d-rotation":  { label: "8D Rotation",     path: "orbit",    bass: 0.4,  clarity: 0.5, reverb: 0.25, speed: 0.15, intensity: 1 },
  "slow-ambient": { label: "Slow Ambient",    path: "sine",     bass: 0.3,  clarity: 0.3, reverb: 0.5,  speed: 0.05, intensity: 0.6 },
  "fast-spin":    { label: "Fast Spin",       path: "orbit",    bass: 0.5,  clarity: 0.5, reverb: 0.15, speed: 0.8,  intensity: 1 },
  "center-focus": { label: "Center Focus",    path: "none",     bass: 0,    clarity: 0,   reverb: 0,    speed: 0,    intensity: 0 },
  "bounce":       { label: "Bounce",          path: "triangle", bass: 0.4,  clarity: 0.4, reverb: 0.2,  speed: 0.3,  intensity: 0.8 },
  "bass-boost":   { label: "Bass Boost",      path: "sine",     bass: 0.85, clarity: 0.3, reverb: 0.15, speed: 0.1,  intensity: 0.7 },
  "concert-hall": { label: "Concert Hall",    path: "orbit",    bass: 0.3,  clarity: 0.6, reverb: 0.7,  speed: 0.08, intensity: 0.5 },
  "studio":       { label: "Studio Monitor",  path: "none",     bass: 0.2,  clarity: 0.7, reverb: 0.1,  speed: 0,    intensity: 0 },
};
const PRESET_ORDER = Object.keys(PRESETS);

const SONGS = [
  { file: "/kulakovka-no-copyright-music-270241.mp3", title: "Kulakovka", preset: "8d-rotation" },
  { file: "/loksii-no-copyright-music-211881.mp3", title: "Loksii", preset: "fast-spin" },
  { file: "/sigmamusicart-no-copyright-music-537751.mp3", title: "Sigma", preset: "concert-hall" },
];

// popup.js describe8DMovement — the exact label wording.
function describe8DMovement(v: number): string {
  if (v <= 0.05) return "Centered";
  if (v < 0.35) return "Subtle";
  if (v < 0.7) return "Balanced";
  if (v < 0.9) return "Wide";
  return "Full orbit";
}

function makeIR(ctx: AudioContext, seconds = 2, decay = 3): AudioBuffer {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const buf = ctx.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
}

export default function SpatialDemo() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [songIndex, setSongIndex] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [presetId, setPresetId] = useState(SONGS[0].preset);
  const [custom, setCustom] = useState(false);
  const [speed, setSpeed] = useState(PRESETS[SONGS[0].preset].speed);
  const [intensity, setIntensity] = useState(PRESETS[SONGS[0].preset].intensity);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webgl, setWebgl] = useState<"pending" | "ok" | "fail">("pending");

  // Live state the audio loop and the 3D scene both read.
  const motionRef = useRef({ path: "orbit" as string, speed: 0.15, intensity: 1, spatial: "hrtf" as "standard" | "surround" | "hrtf", binaural: false });
  const levelRef = useRef(0);
  const phaseRef = useRef(0);
  const abRef = useRef<"normal" | "spatial">("spatial");
  const playingRef = useRef(false);

  const graph = useRef<{
    ctx: AudioContext; lowshelf: BiquadFilterNode; highshelf: BiquadFilterNode; wet: GainNode;
    panner: PannerNode; analyser: AnalyserNode; master: GainNode; freq: Uint8Array<ArrayBuffer>;
  } | null>(null);

  function buildGraph() {
    const el = audioRef.current;
    if (!el || graph.current) return;
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AC();
    const src = ctx.createMediaElementSource(el);
    const lowshelf = ctx.createBiquadFilter();
    lowshelf.type = "lowshelf";
    lowshelf.frequency.value = 180;
    const highshelf = ctx.createBiquadFilter();
    highshelf.type = "highshelf";
    highshelf.frequency.value = 3200;
    const dry = ctx.createGain();
    const wet = ctx.createGain();
    wet.gain.value = 0;
    const conv = ctx.createConvolver();
    conv.buffer = makeIR(ctx);
    const panner = ctx.createPanner();
    panner.panningModel = "HRTF";
    panner.distanceModel = "inverse";
    panner.refDistance = 1;
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    const master = ctx.createGain();

    src.connect(lowshelf);
    lowshelf.connect(highshelf);
    highshelf.connect(dry);
    highshelf.connect(conv);
    conv.connect(wet);
    dry.connect(panner);
    wet.connect(panner);
    panner.connect(analyser);
    analyser.connect(master);
    master.connect(ctx.destination);

    graph.current = { ctx, lowshelf, highshelf, wet, panner, analyser, master, freq: new Uint8Array(analyser.frequencyBinCount) };
    applyNodes();
  }

  // EQ / reverb / panner-model — only changes on on-off or preset (with a brief
  // master dip to hide the panningModel click). Slider drags don't touch nodes.
  function applyNodes() {
    const g = graph.current;
    if (!g) return;
    const p = PRESETS[presetId] ?? PRESETS["8d-rotation"];
    const t = g.ctx.currentTime;
    g.master.gain.cancelScheduledValues(t);
    g.master.gain.setValueAtTime(g.master.gain.value || 1, t);
    g.master.gain.linearRampToValueAtTime(0.0001, t + 0.03);
    g.master.gain.linearRampToValueAtTime(1, t + 0.14);
    g.lowshelf.gain.setTargetAtTime((enabled ? p.bass : 0) * 10, t, 0.05);
    g.highshelf.gain.setTargetAtTime((enabled ? p.clarity : 0) * 9, t, 0.05);
    g.wet.gain.setTargetAtTime((enabled ? p.reverb : 0) * 0.45, t, 0.05);
    g.panner.panningModel = enabled ? "HRTF" : "equalpower";
  }

  // Motion is cheap — update it on every relevant change (incl. slider drags).
  useEffect(() => {
    const p = PRESETS[presetId] ?? PRESETS["8d-rotation"];
    motionRef.current = { path: enabled ? p.path : "none", speed: enabled ? speed : 0, intensity: enabled ? intensity : 0, spatial: "hrtf", binaural: false };
    abRef.current = enabled ? "spatial" : "normal";
  }, [enabled, presetId, speed, intensity]);

  useEffect(() => {
    applyNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, presetId]);

  async function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    try {
      buildGraph();
      bootSceneRef.current();
      await graph.current?.ctx.resume();
      if (el.error) el.load();
      await el.play();
      setError(null);
      setPlaying(true);
    } catch {
      setError("Music file missing — drop the tracks into /public.");
      setPlaying(false);
    }
  }

  function pickSong(i: number) {
    const el = audioRef.current;
    const preset = SONGS[i].preset;
    setSongIndex(i);
    setPresetId(preset);
    setCustom(false);
    setSpeed(PRESETS[preset].speed);
    setIntensity(PRESETS[preset].intensity);
    setEnabled(true);
    if (el) {
      el.src = SONGS[i].file;
      el.load();
      if (playing) el.play().catch(() => setPlaying(false));
    }
  }

  function pickPreset(id: string) {
    if (id === "custom") return;
    setPresetId(id);
    setCustom(false);
    setSpeed(PRESETS[id].speed);
    setIntensity(PRESETS[id].intensity);
    setEnabled(true);
  }

  // Audio loop: advances the shared phase, reads the beat, steers the panner.
  const audioRAF = useRef<number | null>(null);
  function startAudioLoop() {
    if (audioRAF.current !== null) return;
    let last = 0;
    const step = (now: number) => {
      if (!last) last = now;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const m = motionRef.current;
      const off = abRef.current === "normal";
      phaseRef.current = (phaseRef.current + dt * (off ? 0 : m.speed || 0)) % 1;
      const g = graph.current;
      if (g) {
        g.analyser.getByteFrequencyData(g.freq);
        const bass = bassLevel(g.freq);
        const k = bass > levelRef.current ? 0.5 : 0.08;
        levelRef.current += (bass - levelRef.current) * k;
        const tc = g.ctx.currentTime;
        if (off) {
          g.panner.positionX.setTargetAtTime(0, tc, 0.05);
          g.panner.positionZ.setTargetAtTime(-1, tc, 0.05);
          g.panner.positionY.setTargetAtTime(0, tc, 0.05);
        } else {
          const a = 2 * Math.PI * phaseRef.current;
          const spec = PATHS[m.path] ?? PATHS.orbit;
          const sx = pathValue(spec.x, spec.base, a) * m.intensity;
          const sz = pathValue(spec.z, spec.base, a) * m.intensity;
          g.panner.positionX.setTargetAtTime(sx * 3, tc, 0.02);
          g.panner.positionZ.setTargetAtTime(sz * 3, tc, 0.02);
          g.panner.positionY.setTargetAtTime(Math.sin(a) * 0.4 * m.intensity, tc, 0.02);
        }
      } else {
        levelRef.current *= 0.9;
      }
      audioRAF.current = requestAnimationFrame(step);
    };
    audioRAF.current = requestAnimationFrame(step);
  }
  function stopAudioLoop() {
    if (audioRAF.current !== null) cancelAnimationFrame(audioRAF.current);
    audioRAF.current = null;
  }

  // Lazy-boot the three.js scene on scroll-into-view or first Play.
  const sceneRef = useRef<SpatialScene | null>(null);
  const updateActiveRef = useRef<() => void>(() => {});
  const bootSceneRef = useRef<() => void>(() => {});
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let visible = false;
    let disposed = false;
    const getState = () => ({ motion: motionRef.current as SceneMotion, level: levelRef.current, ab: abRef.current, phase: phaseRef.current });

    function updateActive() {
      const active = (visible && !reduced) || playingRef.current;
      sceneRef.current?.setRunning(active);
      if (active) startAudioLoop();
      else stopAudioLoop();
      if (!active && reduced) sceneRef.current?.renderOnce();
    }
    updateActiveRef.current = updateActive;

    async function bootScene() {
      if (sceneRef.current || disposed) return;
      try {
        const { createScene } = await import("./spatialScene");
        if (disposed || sceneRef.current || !canvas) return;
        const sc = createScene(canvas, getState);
        if (!sc) {
          setWebgl("fail");
          return;
        }
        sceneRef.current = sc;
        setWebgl("ok");
        updateActive();
      } catch {
        setWebgl("fail");
      }
    }
    bootSceneRef.current = bootScene;

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) bootScene();
        updateActive();
      },
      { rootMargin: "200px" },
    );
    io.observe(canvas);
    return () => {
      disposed = true;
      io.disconnect();
      sceneRef.current?.dispose();
      sceneRef.current = null;
      stopAudioLoop();
    };
  }, []);

  useEffect(() => {
    playingRef.current = playing;
    updateActiveRef.current();
  }, [playing]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      graph.current?.ctx.close();
    };
  }, []);

  const statusClass = !playing ? "idle" : enabled ? "on" : "idle";
  const statusText = !playing ? "Ready — press play" : enabled ? "Spatial audio active" : "Bypassed — flat stereo";

  return (
    <section id="spatial" className="section">
      <style>{PANEL_CSS}</style>
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#2EC4B6] mb-3">Try the extension · No install</p>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">The actual extension, right here.</h2>
      <p className="text-sm text-[#b3b3b3] mt-3 max-w-lg">
        This is the real 8Dfy control panel running live on a demo track. Put on
        headphones, press play, and flip the switch — the same panel you get after
        installing, spatializing sound in your browser with nothing sent anywhere.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b3b3b3] mr-1">Demo track</span>
        {SONGS.map((s, i) => {
          const on = i === songIndex;
          return (
            <button
              key={s.file}
              type="button"
              onClick={() => pickSong(i)}
              aria-pressed={on}
              className="rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors border"
              style={{ background: on ? "rgba(46,196,182,0.12)" : "var(--surface)", borderColor: on ? "rgba(46,196,182,0.4)" : "var(--border-subtle)", color: on ? "#2EC4B6" : "#fff" }}
            >
              {s.title}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_300px] gap-8 items-start">
        <div className="relative rounded-lg overflow-hidden" style={{ background: "radial-gradient(120% 100% at 50% 0%, #12122400, #0a0a14 80%), var(--surface)", boxShadow: "var(--shadow-card)" }}>
          <div style={{ aspectRatio: "16 / 11" }}>
            <canvas ref={canvasRef} className="block w-full h-full" role="img" aria-label={`3D animation of ${enabled ? PRESETS[presetId]?.label : "flat stereo"} around a listener`} />
          </div>
          {webgl === "fail" && (
            <div className="absolute inset-0 flex items-center justify-center text-center px-6">
              <p className="text-sm text-[#b3b3b3]">Enable WebGL to see the 3D view — audio still works.</p>
            </div>
          )}
          <button
            type="button"
            onClick={toggle}
            aria-pressed={playing}
            className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full pl-3 pr-4 py-2 text-xs font-bold uppercase tracking-[1.4px] transition-transform duration-150 hover:scale-[1.03]"
            style={{ background: playing ? "rgba(255,255,255,0.1)" : "var(--pink)", color: "#fff", backdropFilter: "blur(8px)", boxShadow: "var(--shadow-card)" }}
          >
            {playing ? (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true"><rect x="2" y="1" width="4" height="12" rx="1" /><rect x="8" y="1" width="4" height="12" rx="1" /></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true"><path d="M2 1.5v11a1 1 0 0 0 1.5.87l9-5.5a1 1 0 0 0 0-1.74l-9-5.5A1 1 0 0 0 2 1.5Z" /></svg>
            )}
            {playing ? "Playing" : "Play demo"}
          </button>
          <span className="absolute bottom-5 right-4 text-[10px] uppercase tracking-[1.4px] text-[#b3b3b3] pointer-events-none">🎧 Headphones</span>
          {error && <span className="absolute top-3 left-4 text-[10px] text-[#FF9F1C]">{error}</span>}
          <audio ref={audioRef} src={SONGS[0].file} loop preload="none" crossOrigin="anonymous" />
        </div>

        {/* Faithful reproduction of the real extension popup (audio-spat/popup.html). */}
        <div className="xp mx-auto">
          <div className="xp-header">
            <div className="xp-logo">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="3" /><path d="M7.5 7.5a7 7 0 0 1 9 0" /><path d="M16.5 16.5a7 7 0 0 1-9 0" /><path d="M4.5 5a11 11 0 0 1 15 0" /><path d="M19.5 19a11 11 0 0 1-15 0" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <span className="xp-h1">Audio Spatializer</span>
              <span className="xp-badge">TRIAL</span>
              <div className="xp-sub">by 8Dfy</div>
            </div>
          </div>

          <div className="xp-status">
            <div className={`xp-dot ${statusClass}`} />
            <span className="xp-status-text">{statusText}</span>
          </div>

          <div className={`xp-body ${enabled ? "" : "off"}`}>
            <div className="xp-toggle-row">
              <span className="xp-toggle-label">Spatial Audio</span>
              <label className="xp-switch">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} aria-label="Spatial Audio" />
                <span className="xp-slider-sw" />
              </label>
            </div>

            <div className="xp-group">
              <label>Preset</label>
              <select value={custom ? "custom" : presetId} onChange={(e) => pickPreset(e.target.value)} disabled={!enabled}>
                {PRESET_ORDER.map((id) => (
                  <option key={id} value={id}>{PRESETS[id].label}</option>
                ))}
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="xp-group">
              <label>Speed <span className="val">{speed.toFixed(2)}</span> Hz</label>
              <input type="range" min={0} max={1} step={0.01} value={speed} disabled={!enabled}
                onChange={(e) => { setSpeed(parseFloat(e.target.value)); setCustom(true); }} />
            </div>

            <div className="xp-group">
              <label className="xp-mlabel"><span>8D Movement</span> <span className="val">{describe8DMovement(intensity)}</span></label>
              <input type="range" min={0} max={1} step={0.01} value={intensity} disabled={!enabled}
                aria-label="8D movement amount" aria-valuetext={describe8DMovement(intensity)}
                onChange={(e) => { setIntensity(parseFloat(e.target.value)); setCustom(true); }} />
              <div className="xp-hint">How far the sound moves around your head</div>
            </div>
          </div>

          <div className="xp-footer">
            8Dfy · Client-side processing only
            <a className="xp-getpro" href="#pricing">Unlock every Pro mode at <strong>8dfy.xyz</strong></a>
          </div>
        </div>
      </div>
    </section>
  );
}

type SceneMotion = { path: string; speed: number; intensity: number; spatial: "standard" | "surround" | "hrtf"; binaural: boolean };

const PANEL_CSS = `
.xp{width:300px;max-width:100%;background:#0A0A10;color:#e8e8e8;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13px;border-radius:14px;overflow:hidden;border:1px solid rgba(255,255,255,0.07);box-shadow:0 20px 50px rgba(0,0,0,0.5)}
.xp-header{padding:16px;border-bottom:1px solid rgba(255,255,255,0.05);display:flex;align-items:center;gap:10px;background:linear-gradient(180deg,rgba(255,51,102,0.05),transparent)}
.xp-logo{width:28px;height:28px;border-radius:8px;flex:none;background:linear-gradient(135deg,#FF3366,#FF9F1C);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px rgba(255,51,102,0.25)}
.xp-h1{font-size:14px;font-weight:700;background:linear-gradient(135deg,#FF3366,#FF9F1C);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent}
.xp-sub{font-size:10px;color:rgba(255,255,255,0.3);margin-top:1px}
.xp-badge{display:inline-block;font-size:8px;font-weight:800;letter-spacing:.5px;padding:1px 5px;border-radius:4px;background:rgba(46,196,182,0.14);border:1px solid rgba(46,196,182,0.35);color:#2EC4B6;margin-left:6px;vertical-align:middle}
.xp-status{padding:12px 16px;border-bottom:1px solid rgba(255,255,255,0.05);display:flex;align-items:center;gap:8px}
.xp-dot{width:8px;height:8px;border-radius:50%;flex:none;background:#444;transition:background .3s}
.xp-dot.idle{background:#2EC4B6}
.xp-dot.on{background:#FF3366;animation:xppulse 2s infinite}
@keyframes xppulse{0%,100%{box-shadow:0 0 0 0 rgba(255,51,102,0.4)}50%{box-shadow:0 0 0 5px rgba(255,51,102,0)}}
.xp-status-text{font-size:11px;color:rgba(255,255,255,0.45)}
.xp-body{padding:16px;transition:opacity .2s}
.xp-body.off{opacity:.55}
.xp-group{margin-bottom:14px}
.xp-group>label{display:block;font-size:10px;font-weight:600;color:rgba(255,255,255,0.35);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px}
.xp-group label .val{color:#FF6B8A;text-transform:none;letter-spacing:0;font-weight:700}
.xp-mlabel{display:flex;align-items:baseline;justify-content:space-between}
.xp-hint{margin-top:6px;color:rgba(255,255,255,0.3);font-size:10px;line-height:1.4}
.xp-toggle-row{display:flex;align-items:center;justify-content:space-between;padding:10px 14px;background:rgba(255,255,255,0.02);border:1px solid rgba(255,255,255,0.05);border-radius:10px;margin-bottom:14px}
.xp-toggle-label{font-size:13px;font-weight:600}
.xp-switch{position:relative;width:40px;height:22px;display:inline-block;flex:none}
.xp-switch input{opacity:0;width:0;height:0}
.xp-slider-sw{position:absolute;inset:0;background:rgba(255,255,255,0.08);border-radius:11px;cursor:pointer;transition:background .25s}
.xp-slider-sw::before{content:'';position:absolute;width:16px;height:16px;left:3px;top:3px;background:#fff;border-radius:50%;transition:transform .25s}
.xp-switch input:checked+.xp-slider-sw{background:#FF3366;box-shadow:0 0 12px rgba(255,51,102,0.3)}
.xp-switch input:checked+.xp-slider-sw::before{transform:translateX(18px)}
.xp select{width:100%;padding:8px 10px;border:1px solid rgba(255,255,255,0.08);border-radius:8px;background:rgba(255,255,255,0.03);color:#d4d4d4;font-size:12px;font-family:inherit;cursor:pointer;outline:none;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%23FF3366'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center}
.xp select:disabled,.xp input:disabled{opacity:.5;cursor:not-allowed}
.xp select option{background:#12121a;color:#d4d4d4}
.xp input[type=range]{width:100%;height:4px;border-radius:2px;background:rgba(255,255,255,0.06);outline:none;-webkit-appearance:none;appearance:none;cursor:pointer}
.xp input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;border-radius:50%;background:#FF3366;border:2px solid rgba(255,255,255,0.12);cursor:pointer;transition:transform .15s,box-shadow .15s}
.xp input[type=range]::-webkit-slider-thumb:hover{transform:scale(1.2);box-shadow:0 0 10px rgba(255,51,102,0.5)}
.xp input[type=range]::-moz-range-thumb{width:14px;height:14px;border-radius:50%;background:#FF3366;border:2px solid rgba(255,255,255,0.12);cursor:pointer}
.xp-footer{padding:10px 16px;border-top:1px solid rgba(255,255,255,0.04);font-size:10px;color:rgba(255,255,255,0.28);text-align:center}
.xp-getpro{display:block;margin-top:6px;color:rgba(255,255,255,0.4);text-decoration:none}
.xp-getpro:hover{color:#FF3366}
.xp-getpro strong{background:linear-gradient(135deg,#FF3366,#FF9F1C);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;font-weight:700}
`;
